import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { uploadFileWithProgress, deleteFile } from '../../firebase/r2storage';
import { Plus, Edit2, Trash2, Search, Filter, Video, Lightbulb, BookOpen, ExternalLink, Save, X, Upload, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { id: 'science', name: 'Sciences', emoji: '⚛️' },
  { id: 'arts', name: 'Arts & Design', emoji: '🎨' },
  { id: 'tech', name: 'Technology', emoji: '💻' },
  { id: 'humanities', name: 'Humanities', emoji: '🌍' },
  { id: 'life', name: 'Life Skills', emoji: '❤️' },
  { id: 'creative', name: 'Creative', emoji: '💡' }
];

const CONTENT_TYPES = [
  { id: 'video', name: 'Video', icon: Video, color: 'red' },
  { id: 'interactive', name: 'Interactive', icon: Lightbulb, color: 'purple' },
  { id: 'article', name: 'Article', icon: BookOpen, color: 'blue' },
  { id: 'project', name: 'Project', icon: ExternalLink, color: 'green' },
  { id: 'audio', name: 'Audio', icon: Video, color: 'orange' }
];

const UnschoolingContentManager = () => {
  const [content, setContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableTopics, setAvailableTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoFile, setVideoFile] = useState(null);

  const [formData, setFormData] = useState({
    category: '',
    topic: '',
    contentType: 'video',
    title: '',
    description: '',
    duration: '',
    url: '',
    videoUrl: '',
    difficulty: 'beginner',
    tags: '',
    isActive: true
  });

  useEffect(() => {
    loadCategories();
    loadContent();
  }, []);

  useEffect(() => {
    filterContent();
  }, [content, searchQuery, filterCategory, filterType]);

  useEffect(() => {
    // Update available topics when category changes
    if (formData.category) {
      const selectedCategory = categories.find(c => c.id === formData.category);
      if (selectedCategory && selectedCategory.topics) {
        setAvailableTopics(selectedCategory.topics.filter(t => t.isActive !== false));
      } else {
        setAvailableTopics([]);
      }
    }
  }, [formData.category, categories]);

  const loadCategories = async () => {
    try {
      const categoriesRef = collection(db, 'unschooling_categories');
      const q = query(categoriesRef, orderBy('order', 'asc'));
      const categoriesSnap = await getDocs(q);
      
      const categoriesList = [];
      categoriesSnap.forEach(doc => {
        const data = doc.data();
        // Only include active categories
        if (data.isActive !== false) {
          categoriesList.push({
            id: data.id,
            name: data.name,
            emoji: data.emoji,
            topics: data.topics || []
          });
        }
      });
      
      setCategories(categoriesList);
      
      // Set default category if available
      if (categoriesList.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: categoriesList[0].id }));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const loadContent = async () => {
    try {
      setLoading(true);
      const contentRef = collection(db, 'unschooling_content');
      const contentSnap = await getDocs(contentRef);
      
      const contentList = [];
      contentSnap.forEach(doc => {
        contentList.push({ id: doc.id, ...doc.data() });
      });
      
      setContent(contentList);
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const filterContent = () => {
    let filtered = [...content];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(item => item.category === filterCategory);
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.contentType === filterType);
    }

    setFilteredContent(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.topic) {
      toast.error('Please select category and topic');
      return;
    }

    try {
      let videoUrl = formData.videoUrl || formData.url;

      // Upload video if file is selected
      if (videoFile && formData.contentType === 'video') {
        setUploadingVideo(true);
        videoUrl = await uploadVideo(videoFile);
        setUploadingVideo(false);
      }

      // Auto-generate thumbnail based on category
      const categoryData = categories.find(c => c.id === formData.category);
      const thumbnail = categoryData?.emoji || '📚';

      const contentData = {
        ...formData,
        thumbnail,
        videoUrl: formData.contentType === 'video' ? videoUrl : '',
        url: formData.contentType !== 'video' ? formData.url : videoUrl,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        createdAt: editingContent ? editingContent.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (editingContent) {
        // Update existing content
        const contentRef = doc(db, 'unschooling_content', editingContent.id);
        await updateDoc(contentRef, contentData);
        toast.success('Content updated successfully');
      } else {
        // Create new content
        await addDoc(collection(db, 'unschooling_content'), contentData);
        toast.success('Content created successfully');
      }

      setShowModal(false);
      setEditingContent(null);
      setVideoFile(null);
      setUploadProgress(0);
      resetForm();
      loadContent();
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
      setUploadingVideo(false);
    }
  };

  const uploadVideo = async (file) => {
    return new Promise((resolve, reject) => {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const filePath = `unschooling_videos/${fileName}`;

      uploadFileWithProgress(
        file,
        filePath,
        (progress) => {
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          toast.error('Failed to upload video');
          reject(error);
        },
        (url) => {
          resolve(url);
        }
      );
    });
  };

  const handleEdit = (item) => {
    setEditingContent(item);
    setFormData({
      category: item.category,
      topic: item.topic,
      contentType: item.contentType,
      title: item.title,
      description: item.description || '',
      duration: item.duration,
      url: item.url || '',
      videoUrl: item.videoUrl || '',
      difficulty: item.difficulty || 'beginner',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : '',
      isActive: item.isActive !== false
    });
    setVideoFile(null);
    setUploadProgress(0);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;

    try {
      await deleteDoc(doc(db, 'unschooling_content', id));
      toast.success('Content deleted successfully');
      loadContent();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    }
  };

  const resetForm = () => {
    setFormData({
      category: categories.length > 0 ? categories[0].id : '',
      topic: '',
      contentType: 'video',
      title: '',
      description: '',
      duration: '',
      url: '',
      videoUrl: '',
      difficulty: 'beginner',
      tags: '',
      isActive: true
    });
    setVideoFile(null);
    setUploadProgress(0);
  };

  const getTypeIcon = (type) => {
    const typeObj = CONTENT_TYPES.find(t => t.id === type);
    return typeObj ? typeObj.icon : BookOpen;
  };

  const getTypeColor = (type) => {
    const typeObj = CONTENT_TYPES.find(t => t.id === type);
    return typeObj ? typeObj.color : 'gray';
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Content Library
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {filteredContent.length} items
          </p>
        </div>
        <button
          onClick={() => {
            setEditingContent(null);
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus size={18} />
          Add Content
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
          ))}
        </select>

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Types</option>
          {CONTENT_TYPES.map(type => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </select>
      </div>

      {/* Content Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-4">Loading content...</p>
        </div>
      ) : filteredContent.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No content found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery || filterCategory !== 'all' || filterType !== 'all'
              ? 'Try adjusting your filters'
              : 'Start by adding your first piece of content'}
          </p>
          {!searchQuery && filterCategory === 'all' && filterType === 'all' && (
            <button
              onClick={() => {
                setEditingContent(null);
                resetForm();
                setShowModal(true);
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Add Content
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredContent.map((item) => {
            const Icon = getTypeIcon(item.contentType);
            const color = getTypeColor(item.contentType);
            
            return (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Thumbnail */}
                <div className={`h-32 bg-gradient-to-br from-${color}-500 to-${color}-600 flex items-center justify-center text-5xl`}>
                  {item.thumbnail}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 bg-${color}-100 dark:bg-${color}-900/30 text-${color}-700 dark:text-${color}-400 text-xs rounded-full`}>
                      {CATEGORIES.find(c => c.id === item.category)?.emoji} {item.topic}
                    </span>
                    <span className="text-xs text-gray-500">{item.duration}</span>
                  </div>

                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                    {item.title}
                  </h4>

                  {item.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {item.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon size={16} className="text-gray-400" />
                      <span className="text-xs text-gray-500 capitalize">{item.contentType}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingContent ? 'Edit Content' : 'Add New Content'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingContent(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Content Type *
                  </label>
                  <select
                    value={formData.contentType}
                    onChange={(e) => setFormData({ ...formData, contentType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    {CONTENT_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Topic *
                </label>
                {availableTopics.length > 0 ? (
                  <select
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Select a topic</option>
                    {availableTopics.map(topic => (
                      <option key={topic.name} value={topic.name}>{topic.name}</option>
                    ))}
                  </select>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    No topics available for this category. Please add topics in the Categories & Topics tab first.
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., The Beauty of Quantum Mechanics"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the content"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Duration *
                  </label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="e.g., 15 min"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Difficulty
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {formData.contentType === 'video' ? 'Video Source' : 'URL (Optional)'}
                </label>
                
                {formData.contentType === 'video' ? (
                  <div className="space-y-3">
                    {/* Video Upload Option */}
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Upload Video File
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              if (file.size > 500 * 1024 * 1024) { // 500MB limit
                                toast.error('Video file must be less than 500MB');
                                e.target.value = '';
                                return;
                              }
                              setVideoFile(file);
                              toast.success(`Selected: ${file.name}`);
                            }
                          }}
                          className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 dark:file:bg-purple-900/30 dark:file:text-purple-400"
                        />
                        {videoFile && (
                          <button
                            type="button"
                            onClick={() => {
                              setVideoFile(null);
                              setUploadProgress(0);
                            }}
                            className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      {videoFile && (
                        <p className="text-xs text-gray-500 mt-1">
                          {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(2)} MB)
                        </p>
                      )}
                      {uploadingVideo && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                            <span>Uploading...</span>
                            <span>{uploadProgress.toFixed(0)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* OR Divider */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                      <span className="text-sm text-gray-500">OR</span>
                      <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                    </div>

                    {/* External URL Option */}
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                        External Video URL (YouTube, Vimeo, etc.)
                      </label>
                      <input
                        type="url"
                        value={formData.url}
                        onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                        placeholder="https://youtube.com/watch?v=..."
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                        disabled={!!videoFile}
                      />
                      {videoFile && (
                        <p className="text-xs text-gray-500 mt-1">
                          Remove uploaded file to use external URL
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g., quantum, physics, science"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
                  Active (visible to students)
                </label>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={uploadingVideo || (formData.contentType === 'video' && !videoFile && !formData.url)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingVideo ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingContent ? 'Update Content' : 'Create Content'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingContent(null);
                    setVideoFile(null);
                    setUploadProgress(0);
                    resetForm();
                  }}
                  disabled={uploadingVideo}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnschoolingContentManager;
